import React from "react";
import { useTranslation } from "react-i18next";
import "./InvoiceCard.scss";

interface InvoiceData {
  totalItems: number;
  SubTotal: number;
  taxVATAmount: number;
  subTotalWithVat: number;
  Shipping: number;
  discount: number;
  InvoiceTotal: number;
}

interface InvoiceCardProps {
  invoiceData: InvoiceData;
  type: "payment" | "recurring";
  onPayNow?: () => void;
  onSetUpRecurring?: () => void;
}

const InvoiceCard: React.FC<InvoiceCardProps> = ({
  invoiceData,
  type,
  onPayNow,
  onSetUpRecurring,
}) => {
  const { t } = useTranslation();

  const {
    totalItems,
    SubTotal,
    taxVATAmount,
    subTotalWithVat,
    Shipping,
    discount,
    InvoiceTotal,
  } = invoiceData;

  const formatAmount = (amount: number) =>
    `${amount} ${t("createInvoice.summary.TND", "TND")}`;

  return (
    <div className="einvoice-card">
      <h2 className="einvoice-title">
        {
          type === "recurring" ? t("createInvoice.invoiceCard.title1", "E-Invoice") : t("createInvoice.invoiceCard.title", "E-Invoice") } 
      </h2>

      <div className="invoice-section">
        <h3 className="section-title">
          {t("createInvoice.invoiceCard.summary", "Invoice summary")}
        </h3>

        <div className="invoice-details">
          <div className="invoice-row">
            <span className="label">
              {t("createInvoice.invoiceCard.totalItems", "Total items")}
            </span>
            <span className="value">{totalItems}</span>
          </div>

          <div className="invoice-row">
            <span className="label">
              {t(
                "createInvoice.invoiceCard.subTotalWithoutVat",
                "Sub total (without VAT)"
              )}
            </span>
            <span className="value">
              {type === "payment"
                ? formatAmount(SubTotal)
                : formatAmount(subTotalWithVat)}
            </span>
          </div>

          <div className="invoice-row">
            <span className="label">
              {t("createInvoice.invoiceCard.vatAmount", "VAT amount")}
            </span>
            <span className="value">{formatAmount(taxVATAmount)}</span>
          </div>

          <div className="invoice-row">
            <span className="label">
              {t(
                "createInvoice.invoiceCard.subTotalWithVat",
                "Sub total (with VAT)"
              )}
            </span>
            <span className="value">{formatAmount(subTotalWithVat)}</span>
          </div>

          {(type === "payment" || type === "recurring") && (
            <>
              <div className="invoice-row">
                <span className="label">
                  {t("createInvoice.invoiceCard.shipping", "Shipping")}
                </span>
                <span className="value">{formatAmount(Shipping)}</span>
              </div>

              <div className="invoice-row">
                <span className="label">
                  {t("createInvoice.invoiceCard.discount", "Discount")}
                </span>
                <span className="value">{formatAmount(discount)}</span>
              </div>
            </>
          )}

          <div className="invoice-row grand-total">
            <span className="label">
              {t("createInvoice.invoiceCard.grandTotal", "Grand total")}
            </span>
            <span className="value">{formatAmount(InvoiceTotal)}</span>
          </div>
        </div>
      </div>

      {type === "payment" && (
        <div className="payment-section">
          <p className="payment-description">
            {t("createInvoice.invoiceCard.payNow.description")}
          </p>
          <button className="btn btn-pay" onClick={onPayNow}>
            {t("createInvoice.invoiceCard.payNow.button", "Pay")}
          </button>
        </div>
      )}

      {type === "recurring" && (
        <div className="confirmation-section">
          <p className="confirmation-description">
            {t(
              "createInvoice.invoiceCard.recurring.description",
              "You are about to make the first payment for the recurring purchase."
            )}
          </p>
          <button className="btn btn-recurring" onClick={onSetUpRecurring}>
            {t(
              "createInvoice.invoiceCard.recurring.button",
              "Set Up Recurring Payment"
            )}
          </button>
        </div>
      )}
    </div>
  );
};

export default InvoiceCard;
